import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { ChakraProps, Flex, Th, Tr, useColorModeValue } from "@chakra-ui/react";
import {
  Header,
  HeaderGroup as HeaderGroupType,
  RowData,
  flexRender,
} from "@tanstack/react-table";
import { byLook } from "theme/look";

const DEFAULT_HEADER_SIZE = 0;

const Sortable = <T extends RowData, V>({
  header,
  isCentered,
}: {
  header: Header<T, V>;
  isCentered: boolean;
}) => {
  const canSort = header.column.getCanSort();

  if (!canSort) {
    return (
      <Flex
        alignItems="center"
        justifyContent={isCentered ? "center" : undefined}
      >
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
      </Flex>
    );
  }

  return (
    <Flex
      cursor="pointer"
      alignItems="center"
      onClick={() => {
        header.column.toggleSorting();
      }}
      justifyContent={isCentered ? "center" : undefined}
    >
      <>
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
        {{
          asc: <TriangleUpIcon ml={2} aria-label="sorted ascending" />,
          desc: <TriangleDownIcon ml={2} aria-label="sorted descending" />,
        }[header.column.getIsSorted() as string] ?? null}
      </>
    </Flex>
  );
};

const HeaderGroup = <T extends RowData>({
  headerGroup,
  isSticky = false,
  isSortDisabled = false,
  isCentered = false,
  verticalAlign = "center",
}: {
  headerGroup: HeaderGroupType<T>;
  isSticky?: boolean;
  isSortDisabled?: boolean;
  isCentered?: boolean;
  verticalAlign?: ChakraProps["verticalAlign"];
}) => {
  // Opaque so sticky headers cover scrolled rows; matches the theme tables.
  const tableHeaderBackground = useColorModeValue(
    byLook("gray.50", "gray.700"),
    byLook("brand.400", "brand.600"),
  );
  const tableHeaderColor = useColorModeValue(
    byLook("gray.500", "gray.100"),
    byLook("gray.400", "gray.100"),
  );

  return (
    <Tr
      rounded={byLook(undefined, "md")}
      key={headerGroup.id}
      style={{ ...(isSticky ? { position: "sticky", top: 0, zIndex: 1 } : {}) }}
    >
      {headerGroup.headers.map((header) => {
        const headerSize = header.getSize();

        return (
          <Th
            colSpan={header.colSpan}
            key={header.id}
            bg={tableHeaderBackground}
            color={tableHeaderColor}
            px={4}
            py={byLook(3, 4)}
            style={{
              minWidth:
                headerSize !== DEFAULT_HEADER_SIZE ? headerSize : undefined,
            }}
            textAlign={isCentered ? "center" : undefined}
            verticalAlign={verticalAlign}
          >
            {!isSortDisabled ? (
              <Sortable header={header} isCentered={isCentered} />
            ) : (
              flexRender(header.column.columnDef.header, header.getContext())
            )}
          </Th>
        );
      })}
    </Tr>
  );
};

export { HeaderGroup };
